import {
  UserAgentApplication,
  ClientAuthError,
  InteractionRequiredAuthError,
  Account,
  AuthResponse,
  Configuration,
} from "msal";
// import { b2cPolicies } from "./policies";
import { b2cPolicies, dev_b2cPolicies } from "./policies";
import { logoutRedirectUri, redirectUri } from "../utils/globalConstants";
import { initFetchRequest, initPostRequest } from "../utils/helperFunctions";
import {
  API_PREFIX,
  API_Scope_Domain,
  MSALClientID,
  API_APP_ID_URI_Identifier,
  isDevelopmentEnvironment,
} from "../utils/globalConstants";

import React from "react";

export const isDevelopment = isDevelopmentEnvironment; // If in development environment

let appConfig;
let msalConfig: Configuration;

appConfig = {
  b2cScopes: [
    API_Scope_Domain + "/" + API_APP_ID_URI_Identifier + "/Bella.Read",
  ],
  webApi: API_PREFIX + "/api/SymptomStudy/filter/dashboard",
};

// Reference: https://github.com/syncweek-react-aad/react-aad/issues/95#issuecomment-513178409
// TODO: Potentially come back and change this later.
const cacheLocation: "localStorage" = "localStorage";

if (isDevelopment) {
    /**********************DEVELOPMENTSERVER MSALCONFIG********************************* */

    // Configuration to initialize MSAL
  msalConfig = {
    auth: {
      clientId: MSALClientID,
      authority: dev_b2cPolicies.authorities.signUpSignIn.authority,
      validateAuthority: false,
      redirectUri: redirectUri,
      postLogoutRedirectUri: logoutRedirectUri,
    },
    cache: {
      cacheLocation,
      storeAuthStateInCookie: true,
    },
  };
} else {
    /**********************PRODUCTION MSALCONFIG********************************* */
    // Configuration to initialize MSAL
  msalConfig = {
    auth: {
      clientId: MSALClientID, // This is your client ID
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      validateAuthority: false,
      redirectUri: redirectUri,
      postLogoutRedirectUri: logoutRedirectUri,
    },
    cache: {
      cacheLocation,
      storeAuthStateInCookie: true,
    },
  };
}

// Request to signin, returns an idToken
const loginRequest = {
  scopes: appConfig.b2cScopes,
  prompt: "login",
  login_hint: "",
};

// Request to acquire a token for resource access
const tokenRequest = {
  scopes: appConfig.b2cScopes,
};

let msalObj: UserAgentApplication;

/**
 * Gets the MSAL instance for internal use.
 * In tests, this can be overridden via the test utilities file.
 */
const getMsalInstance = (): UserAgentApplication => {
  // In test environment, allow override from test utilities
  if (process.env.NODE_ENV === 'test' && typeof global !== 'undefined') {
    const testUtils = (global as any).__msalTestUtils;
    if (testUtils?.getMsalObjForTesting) {
      const testInstance = testUtils.getMsalObjForTesting();
      if (testInstance) return testInstance;
    }
  }
  return msalObj;
};

export const initNewMsalObject = async (
  isChangingPassword: Boolean
): Promise<UserAgentApplication> => {
  // If local storage has returned the user has tried changing password
  if (isChangingPassword) {
    if (isDevelopment) {
      msalConfig.auth.authority =
        dev_b2cPolicies.authorities.changePassword.authority;
    } else {
      msalConfig.auth.authority =
        b2cPolicies.authorities.changePassword.authority;
    }

    try {
      msalObj = msalObj || new UserAgentApplication(msalConfig);
      msalObj.loginRedirect(loginRequest);

      delete window.localStorage["msal.error.description"];
      delete window.localStorage["isDoctor"];
      return msalObj;
    } catch (err: any) {
      console.log(err.message);
    }
  } else {
    msalObj = msalObj || new UserAgentApplication(msalConfig);
  }
  return msalObj;
};

/**
 * Authenticates users using MSAL then retrieves and stores access token. Generates error
 * message at top right if authentication fails.
 *
 */
export const signIn = async () => {
  try {
    const msalObj = await initNewMsalObject(false);
    //console.log("signIn:: msalObj", msalObj);
    if (msalObj.getLoginInProgress()) {
     console.log("signIn:: Login already in progress, skipping redirect.");
      return;
    } else {
      msalObj.loginRedirect(loginRequest);
    }
  } catch (err) {
    console.log(err);
  }
};

/**
 * Gets the account information of the current authenticated user.
 */

export const getAccount = (): Account | null => {
  if (!getMsalInstance()) {
    return null;
  }
  return getMsalInstance().getAccount();
};

// gets the Azure AD username of the current authenticated account
export const getUserName = (): string => {
  return getMsalInstance().getAccount().userName;
};

// gets the display username of the current authenticated account
export const getSignedInUserUsername = (): string => {
  const account = getMsalInstance().getAccount();
  if (
    account.name === undefined ||
    account.name === null ||
    account.name === "unknown"
  ) {
    return account.idTokenClaims.extension_Username;
  } else {
    return account.name;
  }
};

// gets the display name of the current authenticated account
export const getSignedInUserFullName = (): string => {
  const account = getMsalInstance().getAccount();
  return (
    account.idTokenClaims.given_name +
    " " +
    account.idTokenClaims.family_name
  );
};

// gets the display name of the current authenticated account
export const getSignedInUserFirstName = (): string => {
    //console.log("getSignedInUserFirstName:: msalObj", getMsalInstance());
    return getMsalInstance().getAccount().idTokenClaims.given_name;
};

// gets the display name of the current authenticated account
export const getSignedInUserLastName = (): string => {
  return getMsalInstance().getAccount().idTokenClaims.family_name;
};

// gets the accountIdentifier of the current authenticated account
export const getAccountID = (): string => {
  return getMsalInstance().getAccount()?.accountIdentifier;
};

// determines whether the user has an active session
export const isUserSignedIn = () => {
  const account = getMsalInstance().getAccount();
  return account !== null;
};

/**
 * Checks if the current MSAL token is valid/not expired.
 * B2C enforces token expiration server-side (typically 1 hour access tokens). 
 *  
 * @return {boolean} True if user has a valid session, false otherwise
 */
export const hasValidToken = (): boolean => {
  try {
    const msalInstance = getMsalInstance();
    const account = msalInstance.getAccount();
    if (!account) {
      return false;
    }

      // Check if ID token is expired
    const idTokenClaims = account.idTokenClaims as any;
    if (idTokenClaims && idTokenClaims.exp) {
        const expirationTime = idTokenClaims.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      if (currentTime >= expirationTime) {
        console.log("Token expired - session invalid");
        return false;
      }
    }

    return true;
  } catch (err) {
    console.error("Error checking token validity:", err);
    return false;
  }
};

// Triggers user sign out.
export const signOut = () => {
    try {
        // Clear ALL authentication state
    localStorage.removeItem("isDoctor");
    localStorage.removeItem("termsAgreed");
    localStorage.removeItem("msal.error.description");

        // Clear all MSAL-related items (collect keys first to avoid mutation during iteration)
    const msalKeysToRemove = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
    msalKeysToRemove.forEach(key => localStorage.removeItem(key));

        const msalInstance = getMsalInstance();

        // Logout will redirect to B2C logout, then back to postLogoutRedirectUri
    msalInstance.logout();
  } catch (err) {
        console.error(err);
        // Fallback: force redirect to home page
    window.location.href = '/';
  }
};

function getAccessTokenFromUrl(url: string) {
    // Extract the fragment part of the URL
    const fragment = url.split("#")[1];

    // Convert the fragment to an object
    const params = new URLSearchParams(fragment);

    // Get the access token
    const accessToken = params.get("access_token");

  return accessToken;
}
/**
 * Attemps to acquire a user access token silently.
 *
 * @return {AuthResponse} The token.
 */
export const getToken = async (): Promise<
  AuthResponse | undefined | string
> => {
  try {
    if (process.env.NODE_ENV === "test") {
      return Promise.resolve(undefined);
    }
    const msalObj = await initNewMsalObject(false);

    if (msalObj.getLoginInProgress()) {
      console.log("getToken:: Login already in progress, skipping token acquisition.");
      return Promise.resolve(undefined);
    }

    if (!isUserSignedIn()) {
      msalObj.acquireTokenRedirect(tokenRequest);
      return;
    }
      const tokenResponse = await msalObj.acquireTokenSilent(tokenRequest);
      //console.log("getToken:: tokenResponse", tokenResponse.accessToken);
    return tokenResponse;
  } catch (err: any) {
    if (err instanceof ClientAuthError) {
      console.log("getToken:: ClientAuthError - attempting interactive login");
      msalObj.acquireTokenRedirect(tokenRequest);
        return Promise.resolve(undefined); // Redirects; do not continue
    } else if (err instanceof InteractionRequiredAuthError) {
        try {
            // Token captured successfully
        const currentUrl = window.location.href;
        const urlAccessToken = getAccessTokenFromUrl(currentUrl) || "";
        const accessToken = urlAccessToken;
        if (accessToken) {
          console.log({ accessToken });
          return Promise.resolve(accessToken);
        } else {
          msalObj.acquireTokenRedirect(tokenRequest);
        }
            return Promise.resolve(undefined); // Redirects; do not continue
      } catch (err: any) {
        console.log("getToken:: An unexpected error occurred:", err.message);
      }
    }
    console.log("getToken:: An unexpected error occurred:", err.message);
    return Promise.reject(err);
  }
};

export const getAccessToken = async (): Promise<string | undefined> => {
  try {
    if (process.env.NODE_ENV === "test") {
      return;
    }

    const tokenResponse = await getToken();
    if (typeof tokenResponse === "string") {
      console.log({ tokenResponse });
      return tokenResponse;
    }
    if (tokenResponse) {
      return tokenResponse.accessToken;
    }
  } catch (err: any) {
    console.log("getAccessToken:: An unexpected error occurred:", err.message);
  }
};

/**
 * Checks to see if the user is a doctor in the database.
 *
 * @return {Promise<boolean>} A promise containing whether the user is a doctor or not.
 */
export const isUserADoctor = async (): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    const request = initFetchRequest("/api/FoodStudy/isDoctor", accessToken);
    const response = await fetch(request);
    const isDoctor: boolean = await response.json();
    return isDoctor;
  } catch (err) {
    return false;
  }
};

/**
 * Checks to see if the user has accepted the TOS in the database.
 * Uses FoodStudy endpoint to READ TOS status.
 */
export const hasTOSChecked = async (): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    const request = initPostRequest("/api/FoodStudy/checktos", accessToken);

    const response = await fetch(request);
    const checkedTOS: boolean = (await response.text()) === "true";
    return checkedTOS;
  } catch (err) {
    console.error("Error checking TOS status:", err);
    return false;
  }
};

/**
 * Creates an account for the user in the database and adds them to the patient group.
 *
 * @return {Promise<boolean>} A promise containing whether the user has been added
 * to the patient group or not.
 */
export async function createUserDBAndAddToPatientGroup() {
  try {
    const accessToken = await getAccessToken();
    const request = initPostRequest(
      "/Users/CreateUserDBAndAddToPatientGroup",
      accessToken
    );

    const response = await fetch(request);
    if (response.status === 200) {
      return true;
    }
    return false;
  } catch (err) {
    console.error("Error creating user DB and adding to patient group:", err);
    return false;
  }
};


/**
 * Saves the user's TOS acceptance to the database.
 *
 * @return {Promise<boolean>} A promise containing whether the user has checked the TOS or not.
 */
export const checkTOS = async (): Promise<boolean> => {
  try {
    const accessToken = await getAccessToken();
    const request = initPostRequest("/Users/UpdateTOSV2", accessToken);

    const response = await fetch(request);
    return response.ok;
  } catch (err) {
    console.error("Error saving TOS acceptance:", err);
    return false;
  }
};

/**
 * Gets the Signed user ID from the database.
 *
 * @return {Promise<number | null>} A promise containing the user ID.
 */
export const getSignedInUserId = async (): Promise<number | null> => {
  try {
    const accessToken = await getAccessToken();
    const request = initFetchRequest("/Users/GetSignedInUserId", accessToken);
    const response = await fetch(request);
    const userId: number = await response.json();
    return userId;
  } catch (err) {
    console.error("Cannot get user ID: " + err);
    // Handle errors
    return null; // return -1 or any invalid user ID when an error occurs
  }
};

export const AuthContext = React.createContext({
  authID: "",
  userID: "",
});

export const PatientContext = React.createContext({
  patientAuthID: "",
  patientID: "",
});
